/**
 *  @class
 *  @function BlogCarousel
 *  Manages blog posts carousel with arrows
 */

if (!customElements.get('blog-carousel')) {
  class BlogCarousel extends HTMLElement {
    constructor() {
      super();
      this.carousel = null;
    }

    connectedCallback() {
      const init = () => {
        const gridEl = this.querySelector('.blog-posts-grid');
        if (!gridEl) return;

        this.grid = gridEl;
        this.columnsDesktop = parseFloat(gridEl.dataset.columns || 4);

        const initialCards = gridEl.querySelectorAll('.blog-post-card');
        if (initialCards.length > 0) {
          requestAnimationFrame(() => {
            this.initCarousel();
          });
        }

        // Handle resize
        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            this.handleResize();
          }, 250);
        });
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
      } else {
        requestAnimationFrame(init);
      }
    }

    initCarousel() {
      if (!this.grid) return;
      
      if (typeof Flickity === 'undefined') {
        return;
      }

      const loop = this.dataset.loop === 'true';
      const progressBarEl = this.querySelector('.blog-flickity-progress--bar');
      const progressOffset = progressBarEl ? (parseInt(progressBarEl.dataset.initialOffset) || 0) : 0;

      // Destroy existing carousel first
      if (this.carousel) {
        try {
          this.carousel.destroy();
        } catch(e) {
          // Silent fail
        }
        this.carousel = null;
        
        this.grid.classList.remove('flickity-enabled');
        this.classList.remove('has-carousel');
        const gridInner = this.grid.querySelector('.blog-posts-grid-inner');
        if (gridInner) {
          gridInner.classList.remove('flickity-enabled');
          gridInner.style.transform = '';
        }
      }

      const cards = Array.from(this.grid.querySelectorAll('.blog-post-card'));
      const cardsCount = cards.length;
      
      if (cardsCount === 0) {
        return;
      }
      
      // Get columns setting (adjusted for mobile/tablet)
      const isMobile = window.innerWidth <= 767;
      const isTablet = window.innerWidth > 767 && window.innerWidth <= 1067;
      
      let effectiveColumns = this.columnsDesktop;
      
      if (isMobile) {
        effectiveColumns = window.innerWidth <= 480 ? 1.2 : 2;
      } else if (isTablet) {
        if (this.columnsDesktop >= 5) effectiveColumns = 4;
        else if (this.columnsDesktop === 4) effectiveColumns = 3;
        else if (this.columnsDesktop >= 3) effectiveColumns = 2;
      }
      
      // Store as class property for button boundary checks
      this.effectiveColumns = Math.floor(effectiveColumns);
      
      const wrapper = this;
      const navButtons = wrapper.querySelector('.blog-carousel-nav');
      
      // Show/hide navigation based on card count vs columns
      const shouldShowCarousel = cardsCount > Math.floor(effectiveColumns);
      
      if (navButtons) {
        if (shouldShowCarousel && window.innerWidth >= 768) {
          navButtons.style.display = 'block';
          wrapper.classList.add('has-carousel');
        } else {
          navButtons.style.display = 'none';
          wrapper.classList.remove('has-carousel');
        }
      }
      
      // Only create carousel if needed
      if (!shouldShowCarousel) {
        this.grid.classList.remove('flickity-enabled');
        wrapper.classList.remove('has-carousel');
        
        const gridInner = this.grid.querySelector('.blog-posts-grid-inner');
        if (gridInner) {
          gridInner.classList.remove('flickity-enabled');
          gridInner.style.transform = '';
        }
        return;
      }

      try {
        const gridInner = this.grid.querySelector('.blog-posts-grid-inner');
        if (!gridInner) {
          return;
        }

        const self = this;
        let flickityInstance = null;

        flickityInstance = new Flickity(gridInner, {
          cellSelector: '.blog-post-card',
          contain: !loop,
          selectedAttraction: 0.025,
          friction: 0.28,
          imagesLoaded: true,
          prevNextButtons: false,
          pageDots: false,
          wrapAround: loop,
          accessibility: true,
          draggable: '>1',
          dragThreshold: 3,
          groupCells: false,
          cellAlign: loop ? 'center' : (window.innerWidth <= 767 ? 'center' : 'left'),
          freeScroll: false,
          initialIndex: 0,
          setGallerySize: true,
          adaptiveHeight: false,
          percentPosition: false,
          on: {
            ready: function() {
              const cards = gridInner.querySelectorAll('.blog-post-card');
              cards.forEach(card => {
                card.style.display = 'flex';
                card.style.opacity = '1';
              });
              
              self.grid.classList.add('flickity-enabled');
              wrapper.classList.add('has-carousel');
              
              // Ensure nav buttons are visible (desktop only)
              if (navButtons && window.innerWidth >= 768) {
                navButtons.style.display = 'block';
              }
              
              this.resize();
              
              // Align arrows vertically to image height only (ignore text below)
              requestAnimationFrame(() => self.alignNavToImage(navButtons));
            },
            change: function(index) {
              if (navButtons) {
                const prevBtn = navButtons.querySelector('.blog-carousel-button--prev');
                const nextBtn = navButtons.querySelector('.blog-carousel-button--next');
                self.updateCarouselButtons(prevBtn, nextBtn);
              }
              if (progressBarEl && loop) {
                var total = this.slides.length;
                var progress = total <= 1 ? 0 : index / (total - 1);
                progressBarEl.style.width = (progressOffset + progress * (100 - progressOffset)) + '%';
              }
            },
            scroll: function(progress) {
              if (progressBarEl && !loop) {
                progressBarEl.style.width = (progressOffset + Math.max(0, Math.min(1, progress)) * (100 - progressOffset)) + '%';
              }
            }
          }
        });

        this.carousel = flickityInstance;

        if (navButtons) {
          setTimeout(() => {
            this.connectCarouselButtons(navButtons);
          }, 50);
        }

      } catch (e) {
        // Silent fail
      }
    }

    connectCarouselButtons(navButtons) {
      if (!navButtons || !this.carousel) {
        return;
      }

      const nextBtn = navButtons.querySelector('.blog-carousel-button--next');
      const prevBtn = navButtons.querySelector('.blog-carousel-button--prev');
      
      if (!nextBtn || !prevBtn) {
        return;
      }
      
      const carousel = this.carousel;
      
      // Clone and replace buttons to remove old listeners
      const cleanNextBtn = nextBtn.cloneNode(true);
      const cleanPrevBtn = prevBtn.cloneNode(true);
      nextBtn.replaceWith(cleanNextBtn);
      prevBtn.replaceWith(cleanPrevBtn);
      
      cleanNextBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Recalculate effectiveColumns based on current screen size
        this.recalculateEffectiveColumns();
        
        if (this.dataset.loop !== 'true') {
          const lastUsefulIndex = Math.max(0, carousel.cells.length - this.effectiveColumns);
          if (carousel.selectedIndex >= lastUsefulIndex) {
            return;
          }
        }
        
        if (carousel && typeof carousel.next === 'function') {
          carousel.next();
          setTimeout(() => {
            this.updateCarouselButtons(cleanPrevBtn, cleanNextBtn);
          }, 100);
        }
      };
      
      cleanPrevBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Recalculate effectiveColumns based on current screen size
        this.recalculateEffectiveColumns();
        
        if (this.dataset.loop !== 'true' && carousel.selectedIndex <= 0) {
          return;
        }
        
        if (carousel && typeof carousel.previous === 'function') {
          carousel.previous();
          setTimeout(() => {
            this.updateCarouselButtons(cleanPrevBtn, cleanNextBtn);
          }, 100);
        }
      };
      
      this.updateCarouselButtons(cleanPrevBtn, cleanNextBtn);
    }

    updateCarouselButtons(prevBtn, nextBtn) {
      if (!this.carousel || !prevBtn || !nextBtn) return;
      
      if (this.dataset.loop === 'true') {
        prevBtn.classList.remove('is-disabled');
        prevBtn.disabled = false;
        nextBtn.classList.remove('is-disabled');
        nextBtn.disabled = false;
        return;
      }
      
      const selectedIndex = this.carousel.selectedIndex;
      const cellsCount = this.carousel.cells.length;
      const lastUsefulIndex = Math.max(0, cellsCount - (this.effectiveColumns || 1));
      
      // Update prev button
      if (selectedIndex === 0) {
        prevBtn.classList.add('is-disabled');
        prevBtn.disabled = true;
      } else {
        prevBtn.classList.remove('is-disabled');
        prevBtn.disabled = false;
      }
      
      // Update next button
      if (selectedIndex >= lastUsefulIndex) {
        nextBtn.classList.add('is-disabled');
        nextBtn.disabled = true;
      } else {
        nextBtn.classList.remove('is-disabled');
        nextBtn.disabled = false;
      }
    }

    recalculateEffectiveColumns() {
      if (!this.grid) return;
      
      const isMobile = window.innerWidth <= 767;
      const isTablet = window.innerWidth > 767 && window.innerWidth <= 1067;
      
      let effectiveColumns = this.columnsDesktop;
      
      if (isMobile) {
        effectiveColumns = window.innerWidth <= 480 ? 1.2 : 2;
      } else if (isTablet) {
        if (this.columnsDesktop >= 5) effectiveColumns = 4;
        else if (this.columnsDesktop === 4) effectiveColumns = 3;
        else if (this.columnsDesktop >= 3) effectiveColumns = 2;
      }
      
      this.effectiveColumns = Math.floor(effectiveColumns);
    }

    handleResize() {
      if (this.carousel) {
        this.carousel.resize();
      }
      const navButtons = this.querySelector('.blog-carousel-nav');
      if (navButtons) this.alignNavToImage(navButtons);
    }

    alignNavToImage(navButtons) {
      if (!navButtons) return;
      // Find the first rendered image figure in the carousel
      const firstFigure = this.querySelector('.blog-post-card .featured-image');
      if (!firstFigure) return;

      const figureHeight = firstFigure.offsetHeight;
      if (!figureHeight) return;

      // Position nav container: top=0, height=figureHeight
      // Buttons inside use top:50% of this container via CSS
      navButtons.style.top = '0';
      navButtons.style.height = figureHeight + 'px';
    }
  }

  customElements.define('blog-carousel', BlogCarousel);
}
